import { useNavigate } from "react-router-dom";
import Card from "../components/Card";
import { Users, Hospital, Hotel, WavesLadder } from "lucide-react";
import CustomBarChart from "../components/CustomBarChart";
import { useTranslation } from "react-i18next";

const DashboardFilter = ({ data, filter, setFilter }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleChange = (e) => {
    setFilter({ ...filter, [e.target.name]: e.target.value });
  };

  const getCardData = () => {
    if (!data) return [];

    if (filter.section === "all") {
      return [
        {
          label: t("population"),
          value:
            data.population?.ذكور +
            data.population?.إناث +
            data.population?.أطفال +
            data.population?.مُسنين,
          icon: <Users className="w-6 h-6 text-orange-900" />,
        },
        {
          label: t("hospitals"),
          value: data.hospitals,
          icon: <Hospital className="w-6 h-6 text-red-500" />,
        },
        {
          label: t("hotels"),
          value: data.hotels.green_star + data.hotels.non_green_star,
          icon: <Hotel className="w-6 h-6 text-green-500" />,
        },
        {
          label: t("diving_centers"),
          value:
            data.Diving_centres.divingCentersGreen +
            data.Diving_centres.divingCentersNotGreen,
          icon: <WavesLadder className="w-6 h-6 text-green-600" />,
        },
      ];
    }

    switch (filter.section) {
      case "population": {
        const populationLabels = {
          ذكور: t("males"),
          إناث: t("females"),
          أطفال: t("children"),
          مُسنين: t("elderly"),
        };

        if (filter.population === "all") {
          return [
            {
              label: t("population"),
              value:
                data.population?.إناث +
                data.population?.ذكور +
                data.population?.أطفال +
                data.population?.مُسنين,
              icon: <Users className="w-6 h-6 text-orange-900" />,
            },
          ];
        }

        return [
          {
            label: populationLabels[filter.population] || t("population"),
            value: data.population?.[filter.population] ?? 0,
            icon: <Users className="w-6 h-6 text-orange-900" />,
          },
        ];
      }

      case "hospitals":
        return [
          {
            label: t("hospitals"),
            value: data.hospitals ?? 0,
            icon: <Hospital className="w-6 h-6 text-red-500" />,
          },
        ];

      case "hotels":
        return [
          {
            label: t("hotels"),
            value:
              (data.hotels?.green_star || 0) +
              (data.hotels?.non_green_star || 0),
            icon: <Hotel className="w-6 h-6 text-green-500" />,
          },
        ];

      case "diving_centres": {
        const green = data.Diving_centres?.divingCentersGreen || 0;
        const notGreen = data.Diving_centres?.divingCentersNotGreen || 0;

        if (filter.diving_centres === "green") {
          return [
            {
              label: t("green_fins"),
              value: green,
              icon: <WavesLadder className="w-6 h-6 text-green-600" />,
            },
          ];
        } else if (filter.diving_centres === "notGreen") {
          return [
            {
              label: t("non_green_fins"),
              value: notGreen,
              icon: <WavesLadder className="w-6 h-6 text-green-600" />,
            },
          ];
        }

        return [
          {
            label: t("diving_centers"),
            value: green + notGreen,
            icon: <WavesLadder className="w-6 h-6 text-green-600" />,
          },
        ];
      }

      default:
        return [];
    }
  };

  return (
    <>
      <div className="flex flex-col gap-2 text-start">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2 w-full justify-between items-center">
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-2/3">
            <select
              name="section"
              value={filter.section}
              onChange={handleChange}
              className="border p-2 rounded w-full sm:w-1/2 md:w-1/3 lg:w-1/4 dark:bg-gray-600 dark:text-white bg-white text-xs sm:text-sm"
            >
              <option value="all">{t("all")}</option>
              <option value="population">{t("population")}</option>
              <option value="hospitals">{t("hospitals")}</option>
              <option value="hotels">{t("hotels")}</option>
              <option value="diving_centres">{t("diving_centers")}</option>
            </select>

            {filter.section === "population" && (
              <select
                name="population"
                value={filter.population}
                onChange={handleChange}
                className="border p-2 rounded w-full sm:w-1/2 md:w-1/3 lg:w-1/4 dark:bg-gray-600 dark:text-white bg-white text-xs sm:text-sm"
              >
                <option value="all">{t("all")}</option>
                <option value="ذكور">{t("males")}</option>
                <option value="إناث">{t("females")}</option>
                <option value="أطفال">{t("children")}</option>
                <option value="مُسنين">{t("elderly")}</option>
              </select>
            )}
            
            {filter.section === "diving_centres" && (
              <select
                name="diving_centres"
                value={filter.diving_centres}
                onChange={handleChange}
                className="border p-2 rounded w-full sm:w-1/2 md:w-1/3 lg:w-1/4 dark:bg-gray-600 dark:text-white bg-white text-xs sm:text-sm"
              >
                <option value="all">{t("all")}</option>
                <option value="green">{t("green_fins")}</option>
                <option value="notGreen">{t("non_green_fins")}</option>
              </select>
            )}
          </div>
          
          <button
            onClick={() => navigate("/DashboardForm")}
            className="bg-green-500 hover:bg-green-600 transition-colors text-white p-2 rounded w-full sm:w-auto text-xs sm:text-sm font-semibold mt-2 sm:mt-0"
          >
            {t("edit_data")}
          </button>
        </div>
      </div>
      {/* Cards and Bar Chart */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
        {getCardData().map((item, index) => (
          <Card
            key={index}
            icon={item.icon}
            value={item.value}
            label={item.label}
          />
        ))}

        {data &&
          (filter.section === "population" || filter.section === "all") && (
            <div className="col-span-full">
              <CustomBarChart
                data={
                  filter.section === "population" && filter.population !== "all"
                    ? [
                        {
                          group: filter.population === 'ذكور' ? t('males') : filter.population === 'إناث' ? t('females') : filter.population === 'أطفال' ? t('children') : t('elderly'),
                          count: data.population[filter.population],
                        },
                      ]
                    : Object.entries(data.population).map(([key, value]) => ({
                        group: key === 'ذكور' ? t('males') : key === 'إناث' ? t('females') : key === 'أطفال' ? t('children') : t('elderly'),
                        count: value,
                      }))
                }
                xKey="group"
                barKey="count"
                barColor="#60a5fa"
                title={t("population")}
              />
            </div>
          )}
      </div>
    </>
  );
};

export default DashboardFilter;


